import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", paymentStatus: null, eventId: null, amount: null, tries: 0 });

  useEffect(() => {
    if (!sessionId) {
      navigate("/feed");
      return;
    }
    let cancelled = false;

    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", paymentStatus: data.payment_status, eventId: data.event_id, amount: data.amount, tries: attempt });
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", paymentStatus: data.payment_status, eventId: data.event_id, amount: null, tries: attempt });
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState({ status: "timeout", paymentStatus: data.payment_status, eventId: data.event_id, amount: null, tries: attempt });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (err) {
        const code = err.response?.status;
        if (code === 401 || code === 404 || attempt >= MAX_POLLS) {
          setState({ status: code === 401 || code === 404 ? "failed" : "timeout", paymentStatus: null, eventId: null, amount: null, tries: attempt });
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };

    poll(0);
    return () => {
      cancelled = true;
    };
  }, [sessionId, navigate]);

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {state.status === "polling" && (
          <>
            <Loader2 size={48} className="animate-spin mx-auto text-rose-400 mb-4" />
            <h1 className="font-display font-black text-2xl mb-2">Confirming your payment...</h1>
            <p className="text-slate-400 text-sm">Hang on, this takes a couple of seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-400 mb-4" />
            <h1 className="font-display font-black text-3xl mb-2">You're in.</h1>
            <p className="text-slate-400 text-sm mb-6">
              Ticket confirmed{state.amount ? ` for $${state.amount.toFixed(2)}` : ""}. Check your Profile to see it.
            </p>
            <div className="flex gap-2 justify-center">
              {state.eventId && (
                <Link to={`/event/${state.eventId}`} data-testid="payment-view-event" className="px-5 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-sm font-semibold">
                  View event
                </Link>
              )}
              <Link to="/profile" data-testid="payment-view-tickets" className="px-5 py-3 rounded-xl bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold">
                My tickets
              </Link>
            </div>
          </>
        )}
        {state.status === "failed" && (
          <>
            <XCircle size={56} className="mx-auto text-rose-500 mb-4" />
            <h1 className="font-display font-black text-2xl mb-2">Payment didn't go through.</h1>
            <Link to="/feed" className="mt-4 inline-block px-5 py-3 rounded-xl bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold" data-testid="payment-back-feed">Back to feed</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Loader2 size={48} className="mx-auto text-amber-400 mb-4" />
            <h1 className="font-display font-black text-2xl mb-2">Still confirming...</h1>
            <p className="text-slate-400 text-sm mb-6">Your bank is taking a moment. You'll see the ticket in your profile once it lands.</p>
            <Link to="/profile" className="inline-block px-5 py-3 rounded-xl bg-rose-500 hover:bg-rose-400 text-white text-sm font-bold" data-testid="payment-profile">Go to profile</Link>
          </>
        )}
      </div>
    </div>
  );
}
